import { useFrame, useThree } from "@react-three/fiber";
import { type ReactNode, useLayoutEffect, useRef } from "react";
import type { Object3D } from "three";
import {
  createFloorAlignmentTracker,
  updateProductFloorAlignment,
} from "./floorAlignment";
import { isSceneDragActive } from "./productDragScope";
import { markProductSceneChanged } from "./sceneActivity";

interface ProductFloorAlignerProps {
  children: ReactNode;
  floorY?: number;
  scale?: number;
}

type FloorAlignmentTracker = ReturnType<typeof createFloorAlignmentTracker>;

/**
 * Aligns changed product geometry to the floor. Camera-only frames reuse the
 * previous bounds; measurement overlays and drag helpers remain excluded.
 */
export const ProductFloorAligner = ({
  children,
  floorY = 0,
  scale = 1 / 1000,
}: Readonly<ProductFloorAlignerProps>) => {
  const groupRef = useRef<Object3D>(null);
  const scene = useThree((state) => state.scene);
  // Lazily, not `useRef(createFloorAlignmentTracker())`: an argument is
  // evaluated on every render, and the factory allocates a `Box3` (two
  // `Vector3`s) that every render after the first throws away. This component
  // wraps the product tree, so it re-renders on every commit of a resize.
  const trackerRef = useRef<FloorAlignmentTracker | null>(null);
  trackerRef.current ??= createFloorAlignmentTracker();
  const tracker = trackerRef.current;

  useLayoutEffect(() => {
    void floorY;
    void scale;
    markProductSceneChanged(scene);
  }, [scene, floorY, scale]);

  useFrame(() => {
    if (!groupRef.current) return;

    // Not an early return: the floor has to keep measuring the product
    // through a drag, it just must not re-seat it mid-gesture.
    updateProductFloorAlignment(
      scene,
      groupRef.current,
      floorY,
      tracker,
      !isSceneDragActive(scene),
    );
  }, -0.5);

  return (
    <group ref={groupRef} scale={scale}>
      {children}
    </group>
  );
};
